import { PropsWithChildren, Children } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

export const GRID_FIXED_MOBILE_PADDING = 32;
export const GRID_FIXED_PADDING = 60;
const MAX_CONTENT_WIDTH = 1152;

/**
 * key: layout
 * value: number of children
 */
const layoutCounts: { [key: string]: number } = {
    "12": 1,
    "6-6": 2,
    "4-4-4": 3,
    "3-3-3-3": 4,
    "2-2-2-2-2-2": 6,
    "4-8": 2,
    "7-5": 2,
    "3-9": 2,
    "8-4": 2,
    "9-3": 2,
    "3-3-6": 3,
    "6-3-3": 3,
    "8": 1,
    "10": 1,
    "overlay": 12
};

/**
 * key: number of children
 * value: default layout style for that number of children
 */
const defaultLayouts: {
    [k: string]: string;
} = {
    "1": "12",
    "2": "6-6",
    "3": "4-4-4",
    "4": "3-3-3-3",
    "6": "2-2-2-2-2-2"
};

Object.freeze(defaultLayouts);

export type Layout =
    | "12"
    | "6-6"
    | "4-4-4"
    | "3-3-3-3"
    | "2-2-2-2-2-2"
    | "4-8"
    | "7-5"
    | "3-9"
    | "8-4"
    | "9-3"
    | "3-3-6"
    | "6-3-3"
    | "8"
    | "10"
    | "overlay";

type Alignment = "default" | "top" | "bottom" | "center";

export interface RowProps {
    layout?: Layout;
    alignItems?: Alignment;
}

/**
 * @description This component is intended to be used as the uniform
 *  grid layout of all pages on wac-web. These specs should be
 *  the source of truth for page alignment, and its rules should
 *  not be modified for nearly all cases.
 * @param alignItems {string} vertical alignment of children
 * @param layout {string} responsive layout of children. Will attempt to use a default for the given number of children if not supplied.
 * @returns
 */
// eslint-disable-next-line complexity
export const Row = (props: PropsWithChildren<RowProps>) => {
    let { layout } = props;
    const { alignItems } = props;

    const children = Children.toArray(props.children);
    if (layout === undefined) {
        layout = defaultLayouts[children.length] as Layout;
        if (layout === undefined) {
            throw new Error(
                `${children.length} is an invalid number of children`
            );
        }
    } else {
        // @ts-ignore
        if (children.length != layoutCounts[layout]) {
            throw new Error(
                // @ts-ignore
                `Row with layout "${layout}" should have ${layoutCounts[layout]} children`
            );
        }
    }
    const alignItemStyles = {
        ...((!alignItems || alignItems === "default") && {
            alignItems: "stretch"
        }),
        ...(alignItems === "top" && { alignItems: "start" }),
        ...(alignItems === "bottom" && { alignItems: "end" }),
        ...(alignItems === "center" && {
            alignItems: "center"
        })
    };
    const commonLayoutStyles = {
        "display": "grid",
        "justifyContent": "space-between",
        "margin": "auto",
        "maxWidth": "100%",
        "boxSizing": "border-box",
        "paddingInlineStart": `${GRID_FIXED_MOBILE_PADDING}px`,
        "paddingInlineEnd": `${GRID_FIXED_MOBILE_PADDING}px`,
        "gap": "24px",
        "width": "100%",

        [media.only.sm]: {
            paddingInlineStart: `${GRID_FIXED_PADDING}px`,
            paddingInlineEnd: `${GRID_FIXED_PADDING}px`
        },
        [media.above.md]: {
            paddingInlineStart: `${GRID_FIXED_PADDING}px`,
            paddingInlineEnd: `${GRID_FIXED_PADDING}px`,
            width: `${MAX_CONTENT_WIDTH + GRID_FIXED_PADDING * 2}px`,
            gap: "32px"
        },

        // rows nested inside rows have no padding added
        ".row-layout": {
            padding: 0
        },
        // top margin for rows that come directly after another row
        ["+ .row-layout"]: {
            marginBlockStart: "24px",

            [media.above.sm]: {
                marginBlockStart: "32px"
            }
        }
    };

    return (
        <div
            className={`row-layout`}
            css={[
                layout === "12" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)"
                    }),
                layout === "6-6" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.only.sm]: {
                            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)"
                        },
                        [media.above.md]: {
                            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)"
                        }
                    }),
                layout === "4-4-4" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.only.sm]: {
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))"
                        },
                        [media.above.md]: {
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))"
                        }
                    }),
                layout === "3-3-3-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.only.sm]: {
                            gridTemplateColumns: "minmax(0 1fr) minmax(0, 1fr)"
                        },
                        [media.above.md]: {
                            gridTemplateColumns: "repeat(4, minmax(0, 1fr))"
                        }
                    }),
                layout === "2-2-2-2-2-2" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
                        [media.only.sm]: {
                            gridTemplateColumns: "repeat(2, minmax(0, 1fr))"
                        },
                        [media.above.md]: {
                            gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                            gap: "32px"
                        }
                    }),
                layout === "4-8" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.above.sm]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridColumnStart: 1,
                                    gridColumnEnd: 5
                                },
                            "> :last-child": {
                                gridColumnStart: 5,
                                gridColumnEnd: -1
                            }
                        }
                    }),
                layout === "7-5" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.only.sm]: {
                            gridTemplateColumns:
                                "minmax(0px, .5833fr) minmax(0px, 0.4166fr)"
                        },
                        [media.above.md]: {
                            gridTemplateColumns:
                                "minmax(0px, .5833fr) minmax(0px, 0.4166fr)"
                        }
                    }),
                layout === "3-9" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.above.md]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridColumnStart: 1,
                                    gridColumnEnd: 4
                                },
                            "> :last-child": {
                                gridColumnStart: 4,
                                gridColumnEnd: -1
                            }
                        }
                    }),
                layout === "8-4" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.above.sm]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridColumnStart: 1,
                                    gridColumnEnd: 9
                                },
                            "> :last-child": {
                                gridColumnStart: 9,
                                gridColumnEnd: -1
                            }
                        }
                    }),
                layout === "9-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "minmax(0, 1fr)",
                        [media.above.md]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridColumnStart: 1,
                                    gridColumnEnd: 10
                                },
                            "> :last-child": {
                                gridColumnStart: 10,
                                gridColumnEnd: -1
                            }
                        }
                    }),
                layout === "3-3-6" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                        "gridTemplateRows": "minmax(0, 1fr) minmax(0, auto)",
                        /* substitute for :first-child selector due to ssr style injection from compiled */
                        "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                            {
                                gridArea: "1 / 1 / 2 / 7"
                            },
                        /* substitute for :nth-child(2) selector due to ssr style injection from compiled */
                        "> :first-of-type:not(style) ~ *:not(:last-child)": {
                            gridArea: "1 / 7 / 2 / -1"
                        },
                        "> :last-child": {
                            gridArea: "2 / 1 / 3 / -1"
                        },
                        [media.above.md]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            "gridTemplateRows": "auto",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridArea: "1 / 1 / 2 / 4"
                                },
                            /* substitute for :nth-child(2) selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style) ~ *:not(:last-child)":
                                {
                                    gridArea: "1 / 4 / 2 / 7"
                                },
                            "> :last-child": {
                                gridArea: "1 / 7 / 2 / -1"
                            }
                        }
                    }),
                layout === "6-3-3" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                        "gridTemplateRows": "minmax(0, auto) minmax(0, 1fr)",
                        /* substitute for :first-child selector due to ssr style injection from compiled */
                        "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                            {
                                gridArea: "1 / 1 / 2 / -1"
                            },
                        /* substitute for :nth-child(2) selector due to ssr style injection from compiled */
                        "> :first-of-type:not(style) ~ *:not(:last-child)": {
                            gridArea: "2 / 1 / 3 / 7"
                        },
                        "> :last-child": {
                            gridArea: "2 / 7 / 3 / -1"
                        },
                        [media.above.md]: {
                            "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                            /* substitute for :first-child selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style):not(:first-of-type:not(style) ~ *)":
                                {
                                    gridColumnStart: 1,
                                    gridColumnEnd: 7
                                },
                            /* substitute for :nth-child(2) selector due to ssr style injection from compiled */
                            "> :first-of-type:not(style) ~ *:not(:last-child)":
                                {
                                    gridColumnStart: 7,
                                    gridColumnEnd: 10,
                                    gridRow: 1
                                },
                            "> :last-child": {
                                gridColumnStart: 10,
                                gridColumnEnd: -1,
                                gridRow: 1
                            }
                        }
                    }),
                layout === "8" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                        "> *:nth-of-type(1)": {
                            gridColumnStart: 1,
                            gridColumnEnd: -1
                        },
                        [media.only.sm]: {
                            "> *:nth-of-type(1)": {
                                gridColumnStart: "2",
                                gridColumnEnd: "12"
                            }
                        },
                        [media.above.md]: {
                            "> *:nth-of-type(1)": {
                                gridColumnStart: "3",
                                gridColumnEnd: "11"
                            }
                        }
                    }),
                layout === "10" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        "gridTemplateColumns": "repeat(12, minmax(0, 1fr))",
                        "> *:nth-of-type(1)": {
                            gridColumnStart: 1,
                            gridColumnEnd: -1
                        },

                        [media.above.md]: {
                            "> *:nth-of-type(1)": {
                                gridColumnStart: 2,
                                gridColumnEnd: 12
                            }
                        }
                    }),
                layout === "overlay" &&
                    css({
                        ...alignItemStyles,
                        ...commonLayoutStyles,
                        gridTemplateColumns: "repeat(12, minmax(0, 1fr))"
                    })
            ]}
        >
            {children.map((child) => child)}
        </div>
    );
};
